<template>
    <div class="grid">
        <div class="unit whole">
            <h1>Profile</h1>
            <p class="lead">Use the form below to change your name or email address</p>
        </div>
    </div>
    <div class="grid">
        <div class="unit whole">
            <div v-if="teacher" class="panel">
                <div class="unit whole">
                    <v-row>
                        <v-col cols="6">
                            <v-text-field
                                v-model="teacher.username"
                                disabled
                                rounded="xl"
                                variant="outlined"
                                label="Username"
                                :readonly="true"
                            ></v-text-field>
                        </v-col>
                    </v-row>
                </div>

                <v-form v-model="formValid" @submit.prevent="submit">
                    <v-row dense>
                        <v-col cols="6">
                            <v-text-field
                                v-model="teacher.name"
                                :rules="nameRules"
                                rounded="xl"
                                variant="outlined"
                                label="Name"
                                required
                            />
                        </v-col>
                    </v-row>

                    <v-row dense>
                        <v-col cols="6">
                            <v-text-field
                                v-model="teacher.email"
                                :rules="emailRules"
                                rounded="xl"
                                variant="outlined"
                                label="Email"
                                type="email"
                            ></v-text-field>
                        </v-col>
                    </v-row>

                    <v-row dense>
                        <v-col cols="6">
                            <v-checkbox v-model="teacher.use_automated_marking" variant="solo" prepend-icon="mdi-robot"
                                ><template #label
                                    >Use AI-assisted marking.&nbsp;
                                    <a id="stonly-open-ai-assisted-marking" href="#"> Read more.</a></template
                                >
                            </v-checkbox>
                        </v-col>
                    </v-row>

                    <v-row class="mt-n5 mb-5">
                        <v-col class="d-flex">
                            <v-btn color="primary" type="submit" :disabled="!formValid">Save changes</v-btn>
                            <div class="ml-5">
                                <v-btn variant="outlined" @click="showChangePasswordDialog = true">
                                    Change password
                                </v-btn>
                            </div>
                        </v-col>
                    </v-row>
                </v-form>
                <v-snackbar
                    v-model="snackbar"
                    rounded="md"
                    close-on-back
                    close-on-content-click
                    :color="error ? 'error' : 'green-darken-1'"
                    :timeout="5000"
                >
                    <div class="d-flex justify-center">{{ snackbarText }}</div></v-snackbar
                >
            </div>
        </div>
    </div>
    <v-dialog v-model="showChangePasswordDialog" max-width="500">
        <v-card title="Change password">
            <v-form v-model="passwordFormValid" @submit.prevent="changePassword">
                <v-card-text>
                    <input :value="teacher?.username" autocomplete="username" hidden />
                    <v-text-field
                        v-model="oldPassword"
                        :rules="oldPasswordRules"
                        rounded="xl"
                        variant="outlined"
                        label="Old password"
                        :append-inner-icon="oldPasswordVisible ? 'mdi-eye-off' : 'mdi-eye'"
                        :type="oldPasswordVisible ? 'text' : 'password'"
                        @click:append-inner="oldPasswordVisible = !oldPasswordVisible"
                        autocomplete="current-password"
                    ></v-text-field>
                    <v-text-field
                        class="mt-3"
                        v-model="newPassword"
                        :rules="newPasswordRules"
                        rounded="xl"
                        variant="outlined"
                        label="New password"
                        :append-inner-icon="newPasswordVisible ? 'mdi-eye-off' : 'mdi-eye'"
                        :type="newPasswordVisible ? 'text' : 'password'"
                        @click:append-inner="newPasswordVisible = !newPasswordVisible"
                        autocomplete="new-password"
                    ></v-text-field>
                    <v-text-field
                        class="mt-3"
                        v-model="newPassword2"
                        :rules="newPassword2Rules"
                        rounded="xl"
                        variant="outlined"
                        label="Confirm new password"
                        :append-inner-icon="newPassword2Visible ? 'mdi-eye-off' : 'mdi-eye'"
                        :type="newPassword2Visible ? 'text' : 'password'"
                        @click:append-inner="newPassword2Visible = !newPassword2Visible"
                        autocomplete="new-password"
                    ></v-text-field>
                    <v-alert v-if="passwordError" type="error" class="mt-3" variant="outlined">
                        {{ passwordError }}
                    </v-alert>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn @click="showChangePasswordDialog = false">Cancel</v-btn>
                    <v-btn
                        :loading="passwordUpdateInProgress"
                        :disabled="!passwordFormValid"
                        color="primary"
                        type="submit"
                        >Change password</v-btn
                    >
                </v-card-actions>
            </v-form>
        </v-card>
    </v-dialog>
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useTeacherStore } from '~/stores/teacher';
import { FetchError } from 'ofetch';

definePage({ meta: { area: 'teacher-profile' } });
const error = ref();
const snackbar = ref();
const snackbarText = ref('');
const formValid = ref();
const passwordFormValid = ref();
const showChangePasswordDialog = ref(false);
const oldPassword = ref('');
const newPassword = ref('');
const newPassword2 = ref('');
const oldPasswordVisible = ref(false);
const newPasswordVisible = ref(false);
const newPassword2Visible = ref(false);
const passwordError = ref('');
const passwordUpdateInProgress = ref(false);

const nameRules = [(value: string) => !!value || 'Please enter a valid name.'];
const emailRules = [
    (value: string) => {
        if (value) {
            return true;
        }
        return 'Must specify email.';
    },
    (value: string) => {
        if (/.+@.+\..+/.test(value)) {
            return true;
        }
        return 'E-mail must be valid.';
    },
];
const oldPasswordRules = [
    (value: string) => {
        if (value) {
            return true;
        }
        return 'Must specify password.';
    },
];
const newPasswordRules = [
    (value: string) => {
        if (value) {
            return true;
        }
        return 'Must specify password.';
    },
    (value: string) => {
        if (value.length >= 8) {
            return true;
        }
        return 'Must be at least 8 characters.';
    },
    (value: string) => {
        if (value.trim().length === value.length) {
            return true;
        }
        return 'Password cannot start or end with a space.';
    },
];
const newPassword2Rules = [
    (value: string) => {
        if (value === newPassword.value) {
            return true;
        }
        return 'Passwords do not match.';
    },
];

const teacherStore = useTeacherStore();
teacherStore.fetchTeacher();

const { teacher } = storeToRefs(teacherStore);
const { updateTeacher, updatePassword } = teacherStore;

const submit = async () => {
    try {
        await updateTeacher({
            name: teacher.value?.name,
            email: teacher.value?.email,
            use_automated_marking: teacher.value?.use_automated_marking,
        });
        snackbarText.value = 'Profile updated';
    } catch {
        error.value = true;
        snackbarText.value = "Couldn't update profile";
    } finally {
        snackbar.value = true;
    }
};

const changePassword = async () => {
    passwordError.value = '';
    passwordUpdateInProgress.value = true;
    try {
        await updatePassword(oldPassword.value, newPassword.value);
        snackbarText.value = 'Password updated';
        snackbar.value = true;
        showChangePasswordDialog.value = false;
    } catch (err) {
        if (err instanceof FetchError && err.data && (err.data as any).message) {
            passwordError.value = (err.data as any).message;
            return;
        }
        passwordError.value = (err as Error).message;
    } finally {
        passwordUpdateInProgress.value = false;
    }
};

watch(showChangePasswordDialog, (newValue) => {
    if (!newValue) {
        oldPassword.value = '';
        newPassword.value = '';
        newPassword2.value = '';
    }
});
</script>
<style lang="scss" scoped>
:deep(.v-form .v-field--variant-solo .v-label.v-field-label--floating) {
    top: 0;
}
</style>
